import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Collapse,
  Divider,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RemoveRedEye as ReadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

// Theme color for Audit Logs page
const accentColor = '#5D6D7E';

const ACTION_TYPES = ['CREATE', 'READ', 'UPDATE', 'DELETE'];
const RESOURCE_TYPES = [
  'users',
  'groups',
  'permission-sets',
  'permission-set-assignments',
  'aws-accounts',
  'identity-providers',
  'custom-applications',
  'static-tokens',
  'realm-settings',
  'mfa',
  'scim',
  'replication',
  'management-account',
  'admins',
];

// Reusable input styles
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const dialogPaperProps = {
  sx: {
    borderRadius: '12px',
    border: '1px solid #E5E5E5',
    backgroundColor: '#FFFFFF',
    maxWidth: '800px',
  },
};

const AuditLogs = () => {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({ logReadRequests: false });
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    actionTypes: [],
    resourceTypes: [],
    statusFilter: 'all',
    userId: '',
    endpoint: '',
  });

  // Dialogs
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
      };

      if (filters.startDate) {
        apiFilters.startDate = new Date(filters.startDate).toISOString();
      }
      if (filters.endDate) {
        apiFilters.endDate = new Date(filters.endDate).toISOString();
      }
      if (filters.actionTypes.length > 0) {
        apiFilters.actionTypes = filters.actionTypes;
      }
      if (filters.resourceTypes.length > 0) {
        apiFilters.resourceTypes = filters.resourceTypes;
      }
      if (filters.statusFilter && filters.statusFilter !== 'all') {
        apiFilters.statusFilter = filters.statusFilter;
      }
      if (filters.userId) {
        apiFilters.userId = filters.userId;
      }
      if (filters.endpoint) {
        apiFilters.endpoint = filters.endpoint;
      }

      const response = await apiService.getAuditLogs(apiFilters);
      if (response.success) {
        setLogs(response.data?.logs || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.total || 0,
          totalPages: response.data?.totalPages || 0,
        }));
      }
    } catch (err) {
      toast.error('Failed to fetch audit logs');
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getAuditStats(30);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiService.getAuditSettings();
      if (response.success) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchSettings();
  }, [fetchStats, fetchSettings]);

  // Handle settings update
  const handleUpdateSettings = async (logReadRequests) => {
    try {
      const response = await apiService.updateAuditSettings(logReadRequests);
      if (response.success) {
        setSettings({ logReadRequests });
        toast.success(
          logReadRequests
            ? 'Read request logging enabled'
            : 'Read request logging disabled'
        );
      }
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 0 }));
    fetchLogs();
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      actionTypes: [],
      resourceTypes: [],
      statusFilter: 'all',
      userId: '',
      endpoint: '',
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // View log details - fetch full log data
  const handleViewDetails = async (logId) => {
    setDetailLoading(true);
    try {
      const response = await apiService.getAuditLog(logId);
      if (response.success) {
        setSelectedLog(response.data);
      } else {
        toast.error('Failed to load log details');
      }
    } catch (err) {
      toast.error('Failed to load log details');
      console.error('Failed to fetch log details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Get status chip
  const getStatusChip = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) {
      return (
        <Chip
          size="small"
          label={statusCode}
          sx={{
            backgroundColor: 'rgba(39, 174, 96, 0.1)',
            color: '#27AE60',
            fontWeight: 600,
            fontFamily: 'Metropolis, sans-serif',
          }}
          icon={<SuccessIcon sx={{ fontSize: 14, color: '#27AE60' }} />}
        />
      );
    } else if (statusCode >= 400 && statusCode < 500) {
      return (
        <Chip
          size="small"
          label={statusCode}
          sx={{
            backgroundColor: 'rgba(243, 156, 18, 0.1)',
            color: '#F39C12',
            fontWeight: 600,
            fontFamily: 'Metropolis, sans-serif',
          }}
          icon={<WarningIcon sx={{ fontSize: 14, color: '#F39C12' }} />}
        />
      );
    } else if (statusCode >= 500) {
      return (
        <Chip
          size="small"
          label={statusCode}
          sx={{
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            color: '#E74C3C',
            fontWeight: 600,
            fontFamily: 'Metropolis, sans-serif',
          }}
          icon={<ErrorIcon sx={{ fontSize: 14, color: '#E74C3C' }} />}
        />
      );
    }
    return <Chip size="small" label={statusCode} />;
  };

  // Get action chip
  const getActionChip = (actionType) => {
    const config = {
      CREATE: { color: '#27AE60', bg: 'rgba(39, 174, 96, 0.1)', icon: <AddIcon sx={{ fontSize: 14 }} /> },
      READ: { color: '#3498DB', bg: 'rgba(52, 152, 219, 0.1)', icon: <ReadIcon sx={{ fontSize: 14 }} /> },
      UPDATE: { color: '#F39C12', bg: 'rgba(243, 156, 18, 0.1)', icon: <EditIcon sx={{ fontSize: 14 }} /> },
      DELETE: { color: '#E74C3C', bg: 'rgba(231, 76, 60, 0.1)', icon: <DeleteIcon sx={{ fontSize: 14 }} /> },
    };
    const c = config[actionType] || { color: '#666666', bg: '#F5F5F5', icon: null };
    return (
      <Chip
        size="small"
        label={actionType}
        icon={c.icon}
        sx={{
          backgroundColor: c.bg,
          color: c.color,
          fontWeight: 600,
          fontFamily: 'Metropolis, sans-serif',
          '& .MuiChip-icon': { color: c.color },
        }}
      />
    );
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Format resource type for display
  const formatResourceType = (resourceType) => {
    return resourceType
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{ minHeight: '100vh', padding: '32px', backgroundColor: '#F9FBFD' }}
    >
      {/* Header */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: accentColor, width: 48, height: 48 }}>
              <HistoryIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Audit Logs
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {pagination.total} log entries
                {stats?.totalLogs ? ` (${stats.totalLogs} in last 30 days)` : ''}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Tooltip title="Audit Settings">
              <IconButton
                onClick={() => setShowSettings(true)}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8', borderColor: accentColor },
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Toggle Filters">
              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  color: showFilters ? accentColor : '#666666',
                  backgroundColor: showFilters ? `${accentColor}15` : '#F9FBFD',
                  border: `1px solid ${showFilters ? accentColor : '#E5E5E5'}`,
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8' },
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchLogs}
                disabled={loading}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': { backgroundColor: '#F0F4F8' },
                }}
              >
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Stats Cards */}
      <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
      <Grid container spacing={2}>
        {[
          { color: '#27AE60', label: 'Creates (30 days)', value: stats?.logsByAction?.CREATE },
          { color: '#F39C12', label: 'Updates (30 days)', value: stats?.logsByAction?.UPDATE },
          { color: '#E74C3C', label: 'Deletes (30 days)', value: stats?.logsByAction?.DELETE },
          { color: '#C0392B', label: 'Errors (30 days)', value: stats ? (stats.logsByStatus?.clientError || 0) + (stats.logsByStatus?.serverError || 0) : undefined },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card elevation={0} sx={{ border: '1px solid #E5E5E5', borderRadius: '8px' }}>
              <CardContent>
                {stats ? (
                  <Typography variant="h5" sx={{ color: card.color, fontWeight: 600, fontFamily: 'Metropolis, sans-serif' }}>
                    {card.value || 0}
                  </Typography>
                ) : (
                  <CircularProgress size={24} sx={{ color: card.color, my: 0.25 }} />
                )}
                <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      </motion.div>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#253E66',
              mb: 2,
            }}
          >
            Filter Logs
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Date pickers and dropdowns */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                size="small"
                type="date"
                label="Start Date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputStyle, minWidth: 150, flex: '1 1 150px', maxWidth: 180 }}
              />
              <TextField
                size="small"
                type="date"
                label="End Date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ ...inputStyle, minWidth: 150, flex: '1 1 150px', maxWidth: 180 }}
              />
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 140, flex: '1 1 140px', maxWidth: 180 }}>
                <InputLabel>Action Type</InputLabel>
                <Select
                  multiple
                  value={filters.actionTypes}
                  onChange={(e) => setFilters(prev => ({ ...prev, actionTypes: e.target.value }))}
                  label="Action Type"
                  renderValue={(selected) => selected.length > 0 ? selected.join(', ') : ''}
                >
                  {ACTION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 160, flex: '1 1 160px', maxWidth: 220 }}>
                <InputLabel>Resource Type</InputLabel>
                <Select
                  multiple
                  value={filters.resourceTypes}
                  onChange={(e) => setFilters(prev => ({ ...prev, resourceTypes: e.target.value }))}
                  label="Resource Type"
                  renderValue={(selected) => selected.length > 1 ? `${selected.length} selected` : selected.map(formatResourceType).join(', ')}
                >
                  {RESOURCE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatResourceType(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ ...inputStyle, minWidth: 130, flex: '1 1 130px', maxWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.statusFilter}
                  onChange={(e) => setFilters(prev => ({ ...prev, statusFilter: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="success">Success (2xx)</MenuItem>
                  <MenuItem value="error">Errors (4xx, 5xx)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Row 2: Search fields and buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="User"
                value={filters.userId}
                onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
                placeholder="Search by email or username..."
                sx={{ ...inputStyle, minWidth: 220, flex: '1 1 220px', maxWidth: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                label="Endpoint"
                value={filters.endpoint}
                onChange={(e) => setFilters(prev => ({ ...prev, endpoint: e.target.value }))}
                placeholder="Search by endpoint path..."
                sx={{ ...inputStyle, minWidth: 220, flex: '1 1 220px', maxWidth: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999999', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  startIcon={<SearchIcon />}
                  sx={{
                    backgroundColor: accentColor,
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    '&:hover': { backgroundColor: '#4A5A6A' },
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{
                    borderColor: '#E5E5E5',
                    color: '#666666',
                    fontFamily: 'Metropolis, sans-serif',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 2,
                    '&:hover': { borderColor: '#999999', backgroundColor: '#F9FBFD' },
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* Logs Table */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FBFD' }}>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Timestamp
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  User
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Action
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Resource
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Duration
                </TableCell>
                <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} sx={{ color: accentColor }} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                      No audit logs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow
                    key={log.id}
                    hover
                    sx={{
                      '@keyframes fadeInRow': {
                        from: { opacity: 0, transform: 'translateY(8px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      },
                      animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`,
                    }}
                  >
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.userEmail || log.userIdentifier}
                    </TableCell>
                    <TableCell>{getActionChip(log.actionType)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatResourceType(log.resourceType)}
                        variant="outlined"
                        sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.description}
                    </TableCell>
                    <TableCell>{getStatusChip(log.httpStatusCode)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                      {log.durationMs}ms
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(log.id)}
                          sx={{ color: accentColor }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Log Detail Dialog */}
      <Dialog
        open={!!selectedLog || detailLoading}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        {detailLoading ? (
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress sx={{ color: accentColor }} />
          </DialogContent>
        ) : selectedLog && (
          <>
            <DialogTitle
              sx={{
                fontFamily: 'Bitter, Georgia, serif',
                fontWeight: 600,
                color: '#253E66',
                borderBottom: '1px solid #E5E5E5',
              }}
            >
              Audit Log Details
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Timestamp
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {formatTimestamp(selectedLog.timestamp)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Duration
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.durationMs}ms
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    User
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.userEmail || selectedLog.userIdentifier}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Auth Method
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.authMethod}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    HTTP Method
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.httpMethod}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Status Code
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{getStatusChip(selectedLog.httpStatusCode)}</Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Action Type
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>{getActionChip(selectedLog.actionType)}</Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Endpoint
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                    {selectedLog.endpoint}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Request Path
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                    {selectedLog.requestPath}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Description
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.description}
                  </Typography>
                </Grid>
                {selectedLog.requestBody && selectedLog.requestBody !== '' && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                      Request Body
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, backgroundColor: '#F9FBFD', mt: 0.5, borderRadius: '6px' }}
                    >
                      <pre
                        style={{
                          margin: 0,
                          overflow: 'auto',
                          fontSize: '0.75rem',
                          fontFamily: 'monospace',
                          maxHeight: '200px',
                        }}
                      >
                        {(() => {
                          try {
                            return JSON.stringify(JSON.parse(selectedLog.requestBody), null, 2);
                          } catch {
                            return selectedLog.requestBody;
                          }
                        })()}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                {selectedLog.responseSummary && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                      Response Summary
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                      {selectedLog.responseSummary}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    Client IP
                  </Typography>
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif' }}>
                    {selectedLog.clientIp}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif' }}>
                    User Agent
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                    }}
                  >
                    {selectedLog.userAgent}
                  </Typography>
                </Grid>
              </Grid>
              </motion.div>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #E5E5E5', px: 3, py: 2 }}>
              <Button
                onClick={() => setSelectedLog(null)}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        PaperProps={dialogPaperProps}
      >
        <DialogTitle
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            borderBottom: '1px solid #E5E5E5',
          }}
        >
          Audit Log Settings
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.logReadRequests}
                onChange={(e) => handleUpdateSettings(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: accentColor,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: accentColor,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500 }}>
                Log READ requests (GET operations)
              </Typography>
            }
          />
          <Typography
            variant="body2"
            sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', mt: 1, ml: 5 }}
          >
            By default, only CREATE, UPDATE, and DELETE operations are logged. Enable this option to
            also log READ (GET) requests.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#999999', fontFamily: 'Metropolis, sans-serif', mt: 2, ml: 5 }}
          >
            Note: Logs are automatically deleted after 90 days.
          </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E5E5E5', px: 3, py: 2 }}>
          <Button
            onClick={() => setShowSettings(false)}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default AuditLogs;
